/**
 * Every number, date and mask the app prints is formatted here.
 *
 * One place, on purpose: inconsistent money formatting across screens is the
 * fastest way to make a finance app feel amateur. If a screen needs a shape
 * that isn't here, add it here.
 */
import {
  differenceInCalendarDays,
  differenceInCalendarMonths,
  format as formatDateFns,
  isValid,
  parseISO,
} from 'date-fns'

/**
 * How a decimal arrives from the API. ASP.NET emits `decimal` as a JSON number,
 * but the generated types widen it to `number | string`, so every formatter
 * accepts both rather than making forty call sites cast.
 */
export type Decimal = number | string | null | undefined

/** A date or timestamp from the API: `2026-08-14` or `2026-08-14T09:30:00Z`. */
export type ApiDate = string | Date | null | undefined

/** Printed when a value is genuinely absent. Never "0" — absent is not zero. */
export const EM_DASH = '—'

/**
 * Plaid's sign convention: a positive `amount` is money *leaving* the account
 * (D18 — stored unmodified). Aggregates from the API are already normalized, so
 * this only applies to raw transaction amounts.
 */
export type SignConvention = 'natural' | 'plaid'

/** Parses an API decimal. Returns null for absent or unparseable values. */
export function toNumber(value: Decimal): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Flips a raw Plaid amount into "positive means money in" so the rest of the UI
 * only has to reason about one convention.
 */
export function toFlowAmount(value: Decimal, convention: SignConvention = 'natural'): number | null {
  const parsed = toNumber(value)
  if (parsed === null) return null
  return convention === 'plaid' ? -parsed : parsed
}

// Intl formatters are expensive to construct and this app renders thousands of
// figures per screen, so they are built once per option set.
const numberFormatters = new Map<string, Intl.NumberFormat>()

function formatter(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`
  let cached = numberFormatters.get(key)
  if (!cached) {
    cached = new Intl.NumberFormat(locale, options)
    numberFormatters.set(key, cached)
  }
  return cached
}

const LOCALE = 'en-US'
export const DEFAULT_CURRENCY = 'USD'

export interface MoneyFormatOptions {
  /** ISO 4217 code. Accounts carry their own; default is the household base. */
  currency?: string
  /** Decimal places. Defaults to 2; pass 0 for headline figures and axes. */
  decimals?: 0 | 2
  /** Force a leading + on positive figures — used where direction is the point. */
  signDisplay?: Intl.NumberFormatOptions['signDisplay']
  /** Interpret the value with Plaid's sign convention before printing. */
  convention?: SignConvention
  /** What to print when the value is absent. */
  fallback?: string
}

/** `$1,284.30`. The default shape for any figure in the app. */
export function formatMoney(value: Decimal, options: MoneyFormatOptions = {}): string {
  const {
    currency = DEFAULT_CURRENCY,
    decimals = 2,
    signDisplay = 'auto',
    convention = 'natural',
    fallback = EM_DASH,
  } = options

  const amount = toFlowAmount(value, convention)
  if (amount === null) return fallback

  return formatter(LOCALE, {
    style: 'currency',
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    signDisplay,
  }).format(amount)
}

/**
 * `$1.2K`, `$48.9K`, `$1.3M`. For axis ticks and anywhere a column is narrower
 * than the number. Never for a figure someone might act on.
 */
export function formatMoneyCompact(value: Decimal, options: MoneyFormatOptions = {}): string {
  const {
    currency = DEFAULT_CURRENCY,
    signDisplay = 'auto',
    convention = 'natural',
    fallback = EM_DASH,
  } = options

  const amount = toFlowAmount(value, convention)
  if (amount === null) return fallback

  // Below a thousand, compact notation ($932) is identical to the plain form
  // minus the cents, so skip the machinery.
  return formatter(LOCALE, {
    style: 'currency',
    currency,
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: Math.abs(amount) >= 1000 ? 1 : 0,
    signDisplay,
  }).format(amount)
}

/** `1,284` — counts, not money. */
export function formatNumber(value: Decimal, decimals = 0, fallback = EM_DASH): string {
  const parsed = toNumber(value)
  if (parsed === null) return fallback
  return formatter(LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(parsed)
}

export interface PercentFormatOptions {
  decimals?: number
  signDisplay?: Intl.NumberFormatOptions['signDisplay']
  fallback?: string
}

/**
 * For values the API already expresses in percentage points — APRs
 * (`24.99` → `24.99%`), warn thresholds, payoff percents.
 */
export function formatPercent(value: Decimal, options: PercentFormatOptions = {}): string {
  const { decimals = 2, signDisplay = 'auto', fallback = EM_DASH } = options
  const parsed = toNumber(value)
  if (parsed === null) return fallback
  const digits = formatter(LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    signDisplay,
  }).format(parsed)
  return `${digits}%`
}

/**
 * For values the API expresses as a 0–1 ratio — credit utilization, allocation
 * share. `0.42` → `42%`.
 */
export function formatRatio(value: Decimal, options: PercentFormatOptions = {}): string {
  const { decimals = 0, signDisplay = 'auto', fallback = EM_DASH } = options
  const parsed = toNumber(value)
  if (parsed === null) return fallback
  return formatter(LOCALE, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    signDisplay,
  }).format(parsed)
}

/**
 * Parses an API date.
 *
 * `parseISO` is used rather than `new Date(...)` because a bare `2026-08-14`
 * passed to the Date constructor is read as UTC midnight, which renders as the
 * *previous day* anywhere west of Greenwich. Due dates being off by one is not
 * a cosmetic bug in this app.
 */
export function parseDate(value: ApiDate): Date | null {
  if (!value) return null
  const parsed = value instanceof Date ? value : parseISO(value)
  return isValid(parsed) ? parsed : null
}

/** `14 Aug` — the workhorse. */
export function formatDay(value: ApiDate, fallback = EM_DASH): string {
  const date = parseDate(value)
  return date ? formatDateFns(date, 'd MMM') : fallback
}

/** `14 Aug 2026` — anywhere the year could be ambiguous. */
export function formatDate(value: ApiDate, fallback = EM_DASH): string {
  const date = parseDate(value)
  return date ? formatDateFns(date, 'd MMM yyyy') : fallback
}

/** `14 Aug 2026, 09:30`. */
export function formatDateTime(value: ApiDate, fallback = EM_DASH): string {
  const date = parseDate(value)
  return date ? formatDateFns(date, 'd MMM yyyy, HH:mm') : fallback
}

/** `Aug 2026` — month buckets on charts and cashflow rows. */
export function formatMonth(value: ApiDate, fallback = EM_DASH): string {
  const date = parseDate(value)
  return date ? formatDateFns(date, 'MMM yyyy') : fallback
}

/**
 * `Aug` — a month axis tick, where the year is already stated by the period
 * selector above the chart and repeating it on every tick is noise.
 */
export function formatMonthShort(value: ApiDate, fallback = EM_DASH): string {
  const date = parseDate(value)
  return date ? formatDateFns(date, 'MMM') : fallback
}

/** `August 2026` — naming a period, where the abbreviation would read as a tick. */
export function formatMonthLong(value: ApiDate, fallback = EM_DASH): string {
  const date = parseDate(value)
  return date ? formatDateFns(date, 'MMMM yyyy') : fallback
}

/** `Friday` — pairs with `formatDate` in the dashboard's "Today" header. */
export function formatWeekday(value: ApiDate, fallback = EM_DASH): string {
  const date = parseDate(value)
  return date ? formatDateFns(date, 'EEEE') : fallback
}

/** `2026-08-14` — for query strings, never for display. */
export function toApiDate(value: Date): string {
  return formatDateFns(value, 'yyyy-MM-dd')
}

/**
 * Whole calendar days from today. Positive is future.
 * Calendar days, not 24-hour spans: "due tomorrow" must not become "due today"
 * because it is 23 hours away.
 */
export function daysFromToday(value: ApiDate, today: Date = new Date()): number | null {
  const date = parseDate(value)
  return date ? differenceInCalendarDays(date, today) : null
}

export interface RelativeDateOptions {
  /** Verb for a past date: `closed` → "closed 2 days ago". */
  pastVerb?: string
  /** Verb for a future date: `due` → "due in 3 days". Defaults to none ("in 3 days"). */
  futureVerb?: string
  /** Reference point. Injectable so this is testable. */
  today?: Date
  fallback?: string
}

/**
 * `in 3 days`, `2 days ago`, `today`, `tomorrow`, `yesterday`.
 *
 * With verbs: `formatRelativeDate(d, { pastVerb: 'closed' })` gives
 * "closed 2 days ago" / "closed today".
 */
export function formatRelativeDate(value: ApiDate, options: RelativeDateOptions = {}): string {
  const { pastVerb, futureVerb, today = new Date(), fallback = EM_DASH } = options
  const days = daysFromToday(value, today)
  if (days === null) return fallback

  if (days === 0) return join(pastVerb ?? futureVerb, 'today')
  if (days === 1) return join(futureVerb, 'tomorrow')
  if (days === -1) return join(pastVerb, 'yesterday')

  if (days > 0) {
    const phrase = days < 45 ? `in ${days} days` : `in ${relativeMonths(value, today)}`
    return join(futureVerb, phrase)
  }

  const magnitude = Math.abs(days)
  const phrase = magnitude < 45 ? `${magnitude} days ago` : `${relativeMonths(value, today)} ago`
  return join(pastVerb, phrase)
}

function relativeMonths(value: ApiDate, today: Date): string {
  const date = parseDate(value)
  if (!date) return ''
  const months = Math.abs(differenceInCalendarMonths(date, today))
  return months === 1 ? '1 month' : `${months} months`
}

function join(verb: string | undefined, phrase: string): string {
  return verb ? `${verb} ${phrase}` : phrase
}

/**
 * How a due date should read on a row: `Due in 3 days`, `Due today`,
 * `Overdue by 2 days`. Overdue is stated in words as well as color.
 */
export function formatDueDate(value: ApiDate, today: Date = new Date()): string {
  const days = daysFromToday(value, today)
  if (days === null) return 'No due date'
  if (days === 0) return 'Due today'
  if (days === 1) return 'Due tomorrow'
  if (days > 0) return `Due in ${days} days`
  const magnitude = Math.abs(days)
  return magnitude === 1 ? 'Overdue by 1 day' : `Overdue by ${magnitude} days`
}

/**
 * `•••• 4821`. Plaid gives the last four; the bullets stand in for the digits
 * we were never sent, so the mask never masquerades as a full number.
 */
export function formatMask(mask: string | null | undefined, fallback = ''): string {
  if (!mask) return fallback
  return `•••• ${mask.replace(/\D/g, '').slice(-4) || mask}`
}

/** `Sapphire Reserve •••• 4821` — the standard way to name an account in text. */
export function formatAccountLabel(
  name: string,
  mask: string | null | undefined,
): string {
  const suffix = formatMask(mask)
  return suffix ? `${name} ${suffix}` : name
}

/** `4 of 12` style counts for pagination and review queues. */
export function formatCount(count: Decimal, singular: string, plural = `${singular}s`): string {
  const parsed = toNumber(count) ?? 0
  return `${formatNumber(parsed)} ${parsed === 1 ? singular : plural}`
}
